import { LocalDate } from '@js-joda/core';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import {
   PatientEntitlement,
   PatientEntitlementArmyDisabledUpdateCommand,
   PatientEntitlementBloodDonorUpdateCommand,
   PatientEntitlementInjuredVeteranUpdateCommand,
   PatientEntitlementPregnancyUpdateCommand,
   PatientEntitlementValidityOverlapsExistingEntitlementFault,
   PatientEntitlementWarDisabledUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { assertUnreachable, useFormController } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { patientEntitlementsTranslationKeys } from '../../translations';
import { ValidityDateRangeFormValue } from '../patientEntitlements.types';
import { toValidityDateRangeValue } from '../patientEntitlements.utils';

import {
   OnPatientEntitlementDocumentUpdated,
   PatientEntitlementUpdateCommandType,
   PatientEntitlementUpdateFormBean,
   PatientEntitlementUpdateFormController,
} from './PatientEntitlementUpdateForm.types';

export const createValidityDateRange = (
   validFrom: LocalDate | undefined,
   validTo: LocalDate | undefined,
   entitlementType: EntitlementType
): ValidityDateRangeFormValue => {
   if (entitlementType === 'C') {
      return { start: validFrom ?? null, end: validTo ?? null };
   }

   return { issueDate: validFrom ?? null, validityDate: validTo ?? null };
};

type PatientEntitlementFormControllerOptions = {
   patientEntitlement: PatientEntitlement;
   entitlementType: EntitlementType;
};

export const usePatientEntitlementUpdateFormController = (
   options: PatientEntitlementFormControllerOptions
): PatientEntitlementUpdateFormController => {
   return useFormController<PatientEntitlementUpdateFormBean>({
      id: options.patientEntitlement.id,
      confirmingDocumentNumber: options.patientEntitlement.confirmingDocumentNumber,
      validityDateRange: createValidityDateRange(
         options.patientEntitlement.validFrom,
         options.patientEntitlement.validTo,
         options.entitlementType
      ),
   });
};

export const choosePatientEntitlementUpdateCommand = (
   entitlementType: EntitlementType
): PatientEntitlementUpdateCommandType => {
   switch (entitlementType) {
      case 'WE':
         return new PatientEntitlementInjuredVeteranUpdateCommand();
      case 'IB':
         return new PatientEntitlementWarDisabledUpdateCommand();
      case 'IW':
         return new PatientEntitlementArmyDisabledUpdateCommand();
      case 'ZK':
         return new PatientEntitlementBloodDonorUpdateCommand();
      case 'C':
         return new PatientEntitlementPregnancyUpdateCommand();
      case 'DN':
      case 'DZ':
      case 'S':
      case 'IN':
         throw new Error(`Not supported type of patient entitlement document: ${entitlementType}`);
      default:
         return assertUnreachable(entitlementType);
   }
};

type PatientEntitlementUpdateCommandOptions = {
   entitlementType: EntitlementType;
   onPatientEntitlementDocumentUpdated: OnPatientEntitlementDocumentUpdated;
};

export const usePatientEntitlementUpdateCommand = (
   options: PatientEntitlementUpdateCommandOptions
): UseCqrsCommandResult<
   void,
   PatientEntitlementUpdateFormBean,
   PatientEntitlementValidityOverlapsExistingEntitlementFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = choosePatientEntitlementUpdateCommand(options.entitlementType);
         command.id = parameters.id;
         command.confirmingDocumentTypeId = parameters.confirmingDocumentType!.id;
         command.confirmingDocumentNumber = parameters.confirmingDocumentNumber!;
         command.validityDateRange = toValidityDateRangeValue(parameters.validityDateRange)!;
         return command;
      },
      {
         onSuccess: options.onPatientEntitlementDocumentUpdated,
         onFault: {
            'egabinet-patient-frontend-api:PatientEntitlementValidityOverlapsExistingEntitlementFault': () =>
               dispatchSnackbarNegativeEvent(
                  patientEntitlementsTranslationKeys.patientEntitlementValidityOverlapsExistingEntitlementFault
               ),
         },
      }
   );
};
